import { DataLoader } from 'src/app/services/data-loader/data-loader';
import {
    DataLoaderUpliftHelper,
    IDataLoaderUpliftHelperOptions,
} from 'src/app/services/data-loader/data-loader-uplift.helper';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DataLoaderResult } from '../../../../services/data-loader/data-loader-result';
import { standardRun } from '../../../../services/data-loader/utils/standard-run';
import { BiVizDashboardRetention } from '../sql-maps/biviz-dashboard-retention-sql-map';
import { SqlComposer } from 'src/app/utils/query/sql-composer';

export interface RetentionByDayNoDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IQueryCacheService,
        IDataLoaderUpliftHelperOptions {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
    queryCacheService: QueryCacheService;
    /**
     * Pass callback to retrieve max day no for the query.
     * If not provided, the default max day no will be used.
     * @param context - query context
     * @returns Must return a string with integer value or 'unlimited'
     */
    maxDayNoGetter?: (context: IQueryContext) => string;
}

export class RetentionByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    RetentionByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'RetentionByDayNoDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: RetentionByDayNoDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getSalesCumulativeByDayNoFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {

            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardRetention.sqlMap;
                const tableFullPath = generateTableFullPath(
                    resolvedGcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                );

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                let getDefaultMaxDayNo = (context: IQueryContext) => {
                    return (
                        context.settings[
                            QueryContextSettingID.RETENTION_MAX_DAY_NO
                        ] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID.RETENTION_MAX_DAY_NO
                        ]!
                    );
                };

                const maxDayNo = options.maxDayNoGetter
                    ? options.maxDayNoGetter(queryContext)
                    : getDefaultMaxDayNo(queryContext);
                const whereDayNoSql = (d: 'day_no' | 'ad') =>
                    maxDayNo !== 'unlimited' ? `WHERE ${d} <= ${maxDayNo}` : '';

                const {
                    splitSelect,
                    splitGroupBy,
                    where,
                    join,
                    windowRunning,
                } = BiVizDashboardRetention.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    { gcpProjectId: resolvedGcpProjectId },
                );

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH source AS (
  SELECT
    ${splitSelect}
    user_id,
    DATE_DIFF(
      (
        SELECT MAX(first_session_date)
        FROM \`${tableFullPath}\`
      ),
      first_session_date,
      DAY
    ) AS max_day_no,
    (SELECT MAX(x) FROM UNNEST(active_days) x) AS max_active_day,
    active_days
  FROM \`${tableFullPath}\` ds
  ${join}
  ${where}
)

, days AS (
  SELECT day_no
  FROM UNNEST(GENERATE_ARRAY(0, (SELECT MAX(max_day_no) FROM source))) AS day_no
  ${whereDayNoSql('day_no')}
)

, cohort_agg AS (
  SELECT
    ${splitGroupBy}
    max_day_no,
    COUNT(*) AS cnt
  FROM source
  GROUP BY ${splitGroupBy}max_day_no
)

, cohort_sizes AS (
  SELECT
    ${SqlComposer.Prefix('ca', splitGroupBy)}
    d.day_no,
    SUM(ca.cnt) AS cohort_size
  FROM days d
  JOIN cohort_agg ca ON ca.max_day_no >= d.day_no
  GROUP BY ${splitGroupBy} d.day_no
)

, present_counts AS (
  SELECT
    ${splitGroupBy}
    ad AS day_no,
    COUNT(*) AS cnt_present
  FROM source, UNNEST(active_days) AS ad
  ${whereDayNoSql('ad')}
  GROUP BY ${splitGroupBy} day_no
)

, rolling_agg AS (
  SELECT
    ${splitGroupBy}
    LEAST(max_day_no, max_active_day) AS rolling_max,
    COUNT(*) AS cnt
  FROM source
  GROUP BY ${splitGroupBy} rolling_max
)

, rolling_sizes AS (
  SELECT
    ${splitGroupBy}
    d.day_no,
    SUM(ra.cnt) AS cnt_rolling_present
  FROM days d
  JOIN rolling_agg ra ON ra.rolling_max >= d.day_no
  GROUP BY ${splitGroupBy} d.day_no
)

, combined AS (
  SELECT
    ${SqlComposer.Prefix('cs', splitGroupBy)}
    cs.day_no,
    COALESCE(pc.cnt_present, 0)          AS cnt_present,
    COALESCE(rs.cnt_rolling_present, 0)  AS cnt_rolling_present,
    cs.cohort_size
  FROM cohort_sizes cs
  LEFT JOIN present_counts pc
    ON cs.day_no = pc.day_no ${splitGroupBy != '' ? `AND cs.split_group = pc.split_group` : ''}
  LEFT JOIN rolling_sizes rs
    ON cs.day_no = rs.day_no ${splitGroupBy != '' ? `AND cs.split_group = rs.split_group` : ''}
)

SELECT
  ${splitGroupBy}
  day_no,
  cnt_present,
  cnt_rolling_present,
  cohort_size,
  SAFE_DIVIDE(cnt_present, cohort_size)          AS retention,
  SAFE_DIVIDE(cnt_rolling_present, cohort_size)  AS rolling_retention,
  ROUND(SUM(SAFE_DIVIDE(cnt_present, cohort_size)) OVER (${windowRunning}) - 1,  2) AS avg_user_lifetime
FROM combined
ORDER BY ${splitGroupBy}day_no
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                DataLoaderUpliftHelper.processUpliftMetrics(
                    options,
                    queryContext,
                    data
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
